import { useEffect, useState } from "react";
import { motion, useMotionValue, useSpring } from "framer-motion";

const SIZES = { default: 36, link: 64, view: 92, text: 0 };

const modeFor = (el) => {
  if (!el?.closest) return "default";
  if (el.closest("input, textarea, select, iframe")) return "text";
  const hit = el.closest("[data-cursor], a, button, [role=button], label");
  if (!hit) return "default";
  return hit.getAttribute("data-cursor") || "link";
};

export const Cursor = () => {
  const [enabled, setEnabled] = useState(false);
  const [mode, setMode] = useState("default");
  const [visible, setVisible] = useState(false);
  const [down, setDown] = useState(false);
  const x = useMotionValue(-100);
  const y = useMotionValue(-100);
  const rx = useSpring(x, { stiffness: 260, damping: 28, mass: 0.6 });
  const ry = useSpring(y, { stiffness: 260, damping: 28, mass: 0.6 });

  useEffect(() => {
    const mq = window.matchMedia("(hover: hover) and (pointer: fine)");
    const apply = () => setEnabled(mq.matches);
    apply();
    mq.addEventListener("change", apply);
    return () => mq.removeEventListener("change", apply);
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;
    document.documentElement.classList.add("has-cursor");
    const move = (e) => {
      x.set(e.clientX);
      y.set(e.clientY);
      setVisible(true);
    };
    const over = (e) => setMode(modeFor(e.target));
    const leave = () => setVisible(false);
    const press = () => setDown(true);
    const release = () => setDown(false);
    window.addEventListener("mousemove", move, { passive: true });
    document.addEventListener("mouseover", over);
    document.documentElement.addEventListener("mouseleave", leave);
    window.addEventListener("mousedown", press);
    window.addEventListener("mouseup", release);
    return () => {
      document.documentElement.classList.remove("has-cursor");
      window.removeEventListener("mousemove", move);
      document.removeEventListener("mouseover", over);
      document.documentElement.removeEventListener("mouseleave", leave);
      window.removeEventListener("mousedown", press);
      window.removeEventListener("mouseup", release);
    };
  }, [enabled, x, y]);

  if (!enabled) return null;
  const size = SIZES[mode] ?? SIZES.default;
  const show = visible && mode !== "text";

  return (
    <>
      <motion.div
        data-testid="custom-cursor-ring"
        aria-hidden="true"
        className="pointer-events-none fixed left-0 top-0 z-[90] flex items-center justify-center rounded-full border"
        style={{ x: rx, y: ry, translateX: "-50%", translateY: "-50%" }}
        animate={{
          width: size,
          height: size,
          opacity: show ? 1 : 0,
          scale: down ? 0.82 : 1,
          backgroundColor: mode === "view" ? "rgba(200,169,126,1)" : mode === "link" ? "rgba(200,169,126,0.14)" : "rgba(200,169,126,0)",
          borderColor: mode === "view" ? "rgba(200,169,126,0)" : "rgba(200,169,126,0.75)",
        }}
        transition={{ type: "spring", stiffness: 300, damping: 26 }}
      >
        <motion.span
          className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#0a0b0d]"
          animate={{ opacity: mode === "view" ? 1 : 0, scale: mode === "view" ? 1 : 0.6 }}
          transition={{ duration: 0.25 }}
        >
          View
        </motion.span>
      </motion.div>
      <motion.div
        data-testid="custom-cursor-dot"
        aria-hidden="true"
        className="pointer-events-none fixed left-0 top-0 z-[91] h-1.5 w-1.5 rounded-full bg-[#f5f3ef] mix-blend-difference"
        style={{ x, y, translateX: "-50%", translateY: "-50%" }}
        animate={{ opacity: show && mode !== "view" ? 1 : 0 }}
        transition={{ duration: 0.2 }}
      />
    </>
  );
};
